import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { getPlanById, syncPlanWithFileMaker } from '../../../../services/virtualPosService';
import formatters from '../../../../styles/tokens/formatters';
import { ArrowLeft, Edit, RefreshCw, Wallet, Calendar, DollarSign, FileText, Activity } from 'lucide-react';

const VirtualPosPlanDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (id) {
      loadPlan();
    }
  }, [id]);

  const loadPlan = async () => {
    try {
      setLoading(true);
      const planData = await getPlanById(id);
      setPlan(planData.plan); // ✅ Acceder al objeto plan dentro de la respuesta
    } catch (error) {
      console.error('Error al cargar plan:', error);
      alert('Error al cargar el plan');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!plan) return;
    
    try {
      setSyncing(true);
      await syncPlanWithFileMaker(plan);
      alert('Plan sincronizado exitosamente con FileMaker');
    } catch (error) {
      console.error('Error en sincronización:', error);
      alert('Error al sincronizar con FileMaker');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Cargando plan...</div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/payments-admin/virtualpos/planes')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a Planes
          </Button>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-muted-foreground">
              Plan no encontrado
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formatAmount = (amount, currency) => {
    return currency === 'CLP' ? formatters.formatCLP(amount) : `${amount} UF`;
  };

  const getFrequencyLabel = (frequency) => {
    // La API ya devuelve el texto en español, no necesita traducción
    return frequency || 'No especificado';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/payments-admin/virtualpos/planes')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a Planes
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{plan.name}</h1>
            <p className="text-muted-foreground">Detalle del plan VirtualPos</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/payments-admin/virtualpos/planes/${id}/edit`)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </Button>
          <Button
            variant="outline"
            onClick={handleSync}
            disabled={syncing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando...' : 'Sincronizar'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Información Básica */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Información Básica</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Nombre</p>
                <p className="font-medium">{plan.name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">ID</p>
                <p className="font-mono text-sm">{plan.id}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Estado</p>
                <Badge variant={plan.is_active === 'T' ? 'default' : 'secondary'}>
                  {plan.is_active === 'T' ? 'Activo' : 'Inactivo'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Información de Pago */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Información de Pago</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Monto</p>
                <p className="text-2xl font-bold">{formatAmount(plan.amount, plan.currency)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Moneda</p>
                <p className="font-medium">{plan.currency === 'CLP' ? 'Pesos Chilenos' : 'Unidad de Fomento'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Información de Frecuencia */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Frecuencia</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Tipo de Frecuencia</p>
                <p className="font-medium">{getFrequencyLabel(plan.frequency_type)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Información de Suscripción */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Información de Suscripción</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Tipo</p>
                <p className="font-medium">{plan.type || 'No especificado'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Días de Prueba</p>
                <p className="font-medium">{plan.trial_days || 0} días</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Número de Cargos</p>
                <p className="font-medium">{plan.num_charges || 0}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Renovación Automática</p>
                <Badge variant={plan.automatic_renewal === 'T' ? 'default' : 'secondary'}>
                  {plan.automatic_renewal === 'T' ? 'Sí' : 'No'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* URLs y Enlaces */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">URLs y Enlaces</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {plan.suscription_url && (
                <div>
                  <p className="text-sm text-muted-foreground">URL de Suscripción</p>
                  <a 
                    href={plan.suscription_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-sm break-all"
                  >
                    {plan.suscription_url}
                  </a>
                </div>
              )}
              {plan.return_url && plan.return_url.trim() && (
                <div>
                  <p className="text-sm text-muted-foreground">URL de Retorno</p>
                  <p className="text-sm font-mono">{plan.return_url}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Descripción */}
      {plan.description && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Descripción</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{plan.description}</p>
          </CardContent>
        </Card>
      )}

      {/* Información Técnica */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Información Técnica</CardTitle>
          <Activity className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Fecha de Creación</p>
              <p className="font-medium">
                {plan.created_at ? new Date(plan.created_at).toLocaleDateString('es-CL') : 'No disponible'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Última Actualización</p>
              <p className="font-medium">
                {plan.updated_at ? new Date(plan.updated_at).toLocaleDateString('es-CL') : 'No disponible'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Origen</p>
              <Badge variant="outline">VirtualPos</Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Estado de Sincronización</p>
              <Badge variant="secondary">Pendiente</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VirtualPosPlanDetail;